/**
 * What the app can be asked to do, as one list.
 *
 * **Three readers, one entry.** The UI reads `title` and `run`; QueVee reads
 * `description` and `inputSchema` and calls `run`; a command search reads
 * `title` and `keywords`. Three lists drift the first time one of them gains an
 * entry, and the drift is silent — the agent would offer a tool no menu has, or
 * the search would find a command the agent cannot call.
 *
 * `description` is prompt text: it says what the action *does*, because that is
 * what the agent picks by. The same string is a search result's subtitle, which
 * is a useful forcing function — a description too vague for the agent is too
 * vague for a person.
 *
 * ## It is agent-shaped before there is an agent
 *
 * `@qufolio/quevee-client` already defines `ClientTool` and sends a manifest on
 * every connect; `apps/web` registers nothing through it and has no chat surface
 * at all. So this exports the `ClientTool[]` now and is tested without one. A
 * registry that is agent-shaped from the start costs nothing today; retrofitting
 * one is a rewrite.
 *
 * ## Nothing here invents behaviour
 *
 * Every `run` is a dispatch the toolbar or the panel already makes. The registry
 * gives what exists a second and third caller — which is the only reason it can
 * be added after the fact without touching what it describes.
 *
 * ## A refusal is a value, not an exception
 *
 * "Not on a three-chart layout", "that chart is gone", "a browser will not open a
 * window without a click" — all ordinary answers, and all of them have to reach
 * the caller with the *reason*. The UI can show it, the search can grey a row,
 * and {@link clientTools} turns it into a thrown error, which is how the bridge
 * hands a message back to the agent so it can correct itself.
 */

import type { ClientTool, ToolResultBody } from "@qufolio/quevee-client";
import type { ChartGroup, Frequency, PlotStyle, SavedScreenerFilter, ScreenerConfig, ScreenerExpr } from "@qufolio/model";
import {
  linkConditions,
  type CompileExpr,
  type DescribeColumn,
  type DescribeExpr,
  type ScreenLinks,
} from "@qufolio/screener-grid";

import type { IndicatorEntry } from "../api/indicators";
import type { TrackList } from "../api/tracklists";
import { canonicalComposite, committedSymbol } from "./composite";
import { addIndicatorPlan, findEntry, numericSlots, validSmooth, type PlanItem } from "./indicator-phrase";
import { canonicalInterval } from "./interval-mru";
import { resolveGroup } from "./group-phrase";
import { entryForAgent, searchGuide, type GuideEntry } from "./guide-index";
import { firstOutside, refusalMessage } from "./symbol-access";
import { symbolResolver, type SymbolRow } from "./symbol-index";
import type { SymbolRoute } from "./symbol-lock";
import { chartExportCsv, chartExportFileName, barRows, type ChartTable } from "./chart-export";
import {
  describedColumnPatch,
  describedConditionEditPatch,
  describedConditionPatch,
  type Described,
} from "./describe-command";
import type { ScreenerCommands } from "./screener-commands";
import type { WorkspaceAction } from "./workspace-actions";
import {
  PIP_CORNERS,
  PIP_SIZES,
  PIP_VIEWS,
  isDataTab,
  pipFrequencyFor,
  pipOfferedOn,
  swapWithChart,
  type DataTab,
  type PipCorner,
  type PipSize,
  type PipView,
} from "./pip";

/** Everything an action needs from the app, injected rather than imported. */
export interface ActionContext {
  /** The chart the ring is on — what an action targets when none is named. */
  activeChartGroupId: string | undefined;
  /** Every chart on the layout, which is what the PIP's availability turns on. */
  chartIds: readonly string[];
  chartGroup: (chartGroupId: string) => ChartGroup | undefined;
  dispatch: (action: WorkspaceAction) => void;
  /** Mints the ids a new pane, axis and plot need — `mintUlid` in the app. */
  mintId: () => string;
  /**
   * The indicator catalog as it stands, `[]` until it has loaded. A function
   * rather than a value because it arrives after the context is built, and a
   * snapshot taken first would be an empty catalog forever.
   */
  indicators: () => readonly IndicatorEntry[];
  dataPanel: {
    openOn: (chartGroupId: string, tab?: DataTab) => void;
    setTab: (tab: DataTab) => void;
    close: () => void;
    /** Bring a detached panel back into the window. */
    dock: () => void;
    detach: () => void;
  };

  // ── the command search's additions ──────────────────────────────────────
  //
  // Optional, every one: a context without them (a test, a host with no
  // screener) refuses the actions that need them with a reason, rather than
  // every caller having to supply a screener it does not have.

  /** What a chart has loaded — `lib/chart-export.ts`'s registry. */
  chartTable?: (chartGroupId: string) => ChartTable | undefined;
  /** Hands a finished file to the browser. */
  download?: (text: string, fileName: string) => void;
  /** Writes the clipboard. From a click only — see `copySymbol`'s `gesture`. */
  copyText?: (text: string) => void;
  /** The mounted screener's Export / Paste — `lib/screener-commands.ts`. */
  screenerCommands?: () => ScreenerCommands | undefined;
  /** The user's own track lists, `[]` without the feature. */
  trackLists?: () => readonly TrackList[];
  /** Whether track lists exist for this brand and account — the star column's two answers. */
  trackListsOffered?: () => boolean;
  /**
   * The describer, or undefined where it is not offered — no QueVee, or a plan
   * without `feat:describe`. A function for `describeEnabled()`'s reason: the
   * plan arrives with `/auth/me`, after this context is built.
   */
  describer?: () => DescribeExpr | undefined;
  /** The layout's screener section, saved (a hidden one still has a library). */
  screener?: () => { sectionId: string; config: ScreenerConfig } | undefined;
  /**
   * The screener on screen and its Link from screen… binding — what the
   * Conditions tab's picker reads. `links` is undefined while the open screen
   * is a group's screen being authored (decision 5 of screen-links: the server
   * refuses a link there). `saved` is the library as shown, links resolved.
   */
  screenerLinks?: () =>
    | {
        sectionId: string;
        screenRef: string | undefined;
        screenName: string | undefined;
        saved: readonly SavedScreenerFilter[];
        links: ScreenLinks | undefined;
      }
    | undefined;
  /**
   * ScanScript text to a tree — the Conditions editor's compiler (`api/compile-expr`).
   * Undefined on a host with no QueVee, and then the chat cannot add a condition.
   */
  compileExpr?: CompileExpr;
  /** Opens the docked screener editor on a tab — [Edit] after a describe. */
  openScreenerEditor?: (tab: "columns" | "filters") => void;
  /**
   * "Chart this" — `App`'s `chartSymbol`: `routePick` (a locked chart falls
   * through to the next unlocked one) then `setSymbol`, or the lock's toast.
   * Answers where the symbol went, so Go to can say when nothing moved.
   */
  chartSymbol?: (chartGroupId: string, symbol: string) => SymbolRoute;
  /**
   * The symbol index — the plan's universe — or `undefined` until it lands.
   * Absent altogether in a build with no symbol service, where (as in
   * `SymbolDialog`) what was typed is charted unchecked.
   */
  symbols?: () => readonly SymbolRow[] | undefined;
  /**
   * Opens the chat with a first message, or undefined where it is not offered —
   * no chat surface, the brand without QueVee, a plan without `feat:agent`.
   * The palette's Ask AI row only; never a registry action.
   */
  askAi?: () => ((text: string) => void) | undefined;
  /**
   * The user guide's entries, or `undefined` until they land — the palette's
   * Guide rows read it synchronously (`api/guide-index.ts`).
   */
  guide?: () => readonly GuideEntry[] | undefined;
  /** The guide, fetched if need be — what `findInGuide`'s tool form awaits. */
  loadGuide?: () => Promise<readonly GuideEntry[]>;
  /** Opens the guide in a new tab at an anchor read from it. From a click only. */
  openGuideAt?: (anchor: string) => void;
}

/** Ordinary success, or an ordinary refusal carrying its reason. */
export type ActionOutcome = { ok: true; detail?: Record<string, unknown> } | { ok: false; reason: string };

const ok = (detail?: Record<string, unknown>): ActionOutcome =>
  detail ? { ok: true, detail } : { ok: true };
const no = (reason: string): ActionOutcome => ({ ok: false, reason });

/** A period's name read as a column — `addCondition`'s guard. */
const FREQUENCY_WORDS = /^(daily|weekly|monthly|quarterly|yearly|annual|day|week|month|quarter|year)$/i;

export interface Action {
  /** What the agent calls it, and the registry's key. */
  id: string;
  /** What a menu or a search result calls it. */
  title: string;
  /** Prompt text for the agent; subtitle for the search. */
  description: string;
  /** Extra words the search should match — what a person would type instead. */
  keywords: readonly string[];
  /** JSON Schema for `args`. */
  inputSchema: Record<string, unknown>;
  /**
   * Why this cannot be a tool call, when it cannot.
   *
   * Set where the browser requires a real user gesture — a window, a clipboard
   * read or write — and for the two Describe actions, whose input is an answer
   * the command search fetched (an AI call the person asked for) rather than
   * anything an agent could supply. The action still runs from the UI and still
   * appears in the search; only its tool form refuses, with this text, so the
   * agent asks rather than reporting success for something that did not happen.
   */
  gesture?: string;
  /**
   * Whether this context offers the action at all. False hides its palette
   * rows — **the brand masks** (CLAUDE.md, Branding): a track-list command
   * without the feature, a Describe without the plan, is absent rather than
   * listed and refused. `run` still refuses on its own, for the agent.
   */
  available?: (ctx: ActionContext) => boolean;
  /** The row's title when it depends on the context — `Copy symbol — AVGO`. */
  label?: (ctx: ActionContext) => string;
  /**
   * A short palette subtitle in place of `description`, when the row needs to
   * say something only the context knows (`AVGO → clipboard`) or the prose is
   * longer than a row can carry. The agent still reads `description`.
   */
  hint?: (ctx: ActionContext) => string;
  /** How the palette's row behaves when one click is not the whole thought. */
  palette?: PaletteEntry;
  /**
   * The text box's half of a `palette.mode: "text"` row: what pressing Enter
   * asks. Async because it may cost a model call — and only Enter calls it,
   * never a keystroke. The answer offers buttons, each one a `run` with args.
   */
  ask?: (ctx: ActionContext, text: string) => Promise<AskOutcome>;
  /**
   * The values the one required argument may take, when they are data rather
   * than a schema `enum` — the indicator catalog, which arrives from the server
   * and differs by plan. The palette expands these exactly as it expands an
   * enum; the agent still reads `inputSchema` and names one.
   */
  choices?: (ctx: ActionContext) => readonly Choice[];
  /**
   * The palette builds this action's rows from **what is typed** rather than
   * from its schema — Go to from the symbol index, Interval from the toolbar's
   * parser (`typedCommands` in `lib/command-palette.ts`). Neither argument is a
   * set to expand: a ticker is one of fourteen thousand, an interval any count.
   */
  typed?: true;
  /**
   * An answer to read rather than a change to make, and one that may need a
   * fetch — `findInGuide`. The tool form awaits this instead of calling `run`,
   * whose synchronous shape every UI caller relies on.
   */
  lookup?: (ctx: ActionContext, args: Record<string, unknown>) => Promise<ActionOutcome>;
  run: (ctx: ActionContext, args: Record<string, unknown>) => ActionOutcome;
}

/** One value of a data-driven argument, as a palette row offers it. */
export interface Choice {
  value: string;
  /** What the row says after the action's title. */
  label: string;
  /** More words a search should match — the family's name, its category. */
  keywords?: readonly string[];
  /** A sub-search row's subtitle — the indicator's category. */
  detail?: string;
}

/**
 * A palette row that opens a second step in the same box instead of running.
 *
 * - `browse` — a sub-search over the action's `choices`: Add indicator: MACD.
 *   With `browseWhen` false the row runs at once with no args instead — Paste
 *   symbols… asks for a list only when no list is on screen.
 * - `text` — a text box; Enter calls `ask`, and the answer's buttons run.
 */
export interface PaletteEntry {
  mode: "browse" | "text";
  /** The top-level row's title, ending in `…` because it asks something next. */
  title: string;
  /** The box's prefix in the second step — `Add indicator:`. */
  prompt: string;
  /** The second step's placeholder. */
  placeholder?: string;
  browseWhen?: (ctx: ActionContext) => boolean;
  /**
   * Keep the per-choice rows out of the top-level search. Add indicator wants
   * them there (typing `rsi` finds RSI); Paste symbols — My list does not, since
   * a list on screen is used without asking.
   */
  nested?: boolean;
}

/** What a text step's Enter came back with. */
export type AskOutcome =
  | {
      ok: true;
      /** What the answer is — the compiled DSL. */
      preview: string;
      /** One button per way to use it; each runs the action with its args. */
      offers: readonly { label: string; args: Record<string, unknown> }[];
    }
  | { ok: false; reason: string };

// ── argument helpers ────────────────────────────────────────────────────────

const str = (args: Record<string, unknown>, key: string): string | undefined =>
  typeof args[key] === "string" ? (args[key] as string) : undefined;

/**
 * Which chart an action acts on: the one named, or the active one.
 *
 * The same rule the indicator picker and the screener's row cursor already
 * follow, so "add an SMA" and "show me the weekly" target the same chart.
 */
function target(ctx: ActionContext, args: Record<string, unknown>): ChartGroup | undefined {
  const id = str(args, "chartGroupId") ?? ctx.activeChartGroupId;
  return id ? ctx.chartGroup(id) : undefined;
}

/** A chart, or the reason there is not one. */
function withChart(
  ctx: ActionContext,
  args: Record<string, unknown>,
  fn: (group: ChartGroup) => ActionOutcome,
): ActionOutcome {
  const group = target(ctx, args);
  if (!group) return no("there is no chart to act on");
  return fn(group);
}

/** A chart that may carry a PIP, or the reason it may not. */
function withPipChart(
  ctx: ActionContext,
  args: Record<string, unknown>,
  fn: (group: ChartGroup) => ActionOutcome,
): ActionOutcome {
  return withChart(ctx, args, (group) => {
    if (!pipOfferedOn(ctx.chartIds.length)) {
      // With the reason, so the agent can say it rather than retrying: past two
      // charts a cell is small enough that a panel covering a corner costs more
      // than it shows.
      return no(
        `a picture-in-picture panel is only offered on layouts of one or two charts, and this one has ${ctx.chartIds.length}`,
      );
    }
    return fn(group);
  });
}

const one = (name: string, schema: Record<string, unknown>, required = false) => ({
  type: "object",
  properties: { [name]: schema, ...CHART_ARG },
  ...(required ? { required: [name] } : {}),
  additionalProperties: false,
});

// ── stylePlot ───────────────────────────────────────────────────────────────

/** The contract's `PlotStyle.markerSize` bounds — the picker clamps to the same. */
const MARKER_SIZE = { min: 2, max: 40 } as const;
const MARKER_SHAPES = [
  "circle", "triangle", "triangleDown", "arrowUp", "arrowDown", "square",
  "diamond", "cross", "x", "line", "column", "text",
] as const;
const DASH_STYLES = ["solid", "dashed", "dotted"] as const;

/**
 * The panes with `args`' style merged into each named plot, or the reason not.
 *
 * Every id is checked before anything changes, so a call is all or nothing; and
 * the result is one `setPanes`, so one undo — `setPlotStyle` per plot would be
 * one undo each. Style is excluded from `structuralKey`, so this is a repaint.
 */
export function stylePlotsPlan(
  group: ChartGroup,
  args: Record<string, unknown>,
): { panes: ChartGroup["panes"]; plotIds: string[]; style: PlotStyle } | string {
  const ids = Array.isArray(args["plotIds"])
    ? (args["plotIds"] as unknown[]).filter((id): id is string => typeof id === "string")
    : [];
  if (ids.length === 0) return "name the plots to restyle, by id";
  const onChart = new Set(group.panes.flatMap((p) => (p.plots ?? []).map((plot) => plot.id)));
  const missing = ids.filter((id) => !onChart.has(id));
  if (missing.length > 0) return `no plot ${missing.join(", ")} on this chart — use the ids in the chart context`;

  const style: PlotStyle = {};
  const num = (key: string) => (typeof args[key] === "number" && Number.isFinite(args[key]) ? (args[key] as number) : undefined);
  const size = num("markerSize");
  if (size !== undefined) {
    if (size < MARKER_SIZE.min || size > MARKER_SIZE.max) {
      return `marker size runs ${MARKER_SIZE.min} to ${MARKER_SIZE.max} pixels`;
    }
    style.markerSize = size;
  }
  const width = num("thickness");
  if (width !== undefined) {
    if (width < 0 || width > 10) return "line width runs 0 to 10 pixels";
    style.thickness = width;
  }
  const dash = str(args, "dashStyle");
  if (dash !== undefined) {
    if (!(DASH_STYLES as readonly string[]).includes(dash)) return `dash is one of ${DASH_STYLES.join(", ")}`;
    style.dashStyle = dash as PlotStyle["dashStyle"];
  }
  const shape = str(args, "markerShape");
  if (shape !== undefined) {
    if (!(MARKER_SHAPES as readonly string[]).includes(shape)) return `marker shape is one of ${MARKER_SHAPES.join(", ")}`;
    style.markerShape = shape as PlotStyle["markerShape"];
  }
  const stroke = str(args, "stroke")?.trim();
  if (stroke) style.stroke = stroke;
  if (Object.keys(style).length === 0) return "say what to change — marker size, width, dash, shape or colour";

  const named = new Set(ids);
  const panes = group.panes.map((pane) =>
    (pane.plots ?? []).some((plot) => named.has(plot.id))
      ? {
          ...pane,
          plots: (pane.plots ?? []).map((plot) =>
            named.has(plot.id) ? { ...plot, style: { ...plot.style, ...style } } : plot,
          ),
        }
      : pane,
  );
  return { panes, plotIds: ids, style };
}

/**
 * The linkable screen `asked` names — by its name, or `Group · Name` — never
 * the open one. Exact before partial; two partial matches are a question back.
 */
function matchScreen(
  screens: ScreenLinks["screens"],
  asked: string,
  open: string | undefined,
): ScreenLinks["screens"][number] | string {
  const pool = screens.filter((s) => s.key !== open);
  if (pool.length === 0) return "you hold no group screens to link from";
  const norm = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const want = norm(asked);
  const full = (s: (typeof pool)[number]) => norm(`${s.ownerName} ${s.name}`);
  const exact = pool.filter((s) => norm(s.name) === want || full(s) === want);
  if (exact.length === 1) return exact[0]!;
  const partial = (exact.length > 1 ? exact : pool).filter((s) => full(s).includes(want));
  if (partial.length === 1) return partial[0]!;
  const names = (partial.length > 1 ? partial : pool).map((s) => `${s.ownerName} · ${s.name}`).join(", ");
  return partial.length > 1
    ? `more than one screen matches "${asked}": ${names} — say which`
    : `no screen to link from matches "${asked}" — the screens are ${names}`;
}

/** Every chart-targeted action takes the same optional id. */
const CHART_ARG = {
  chartGroupId: {
    type: "string",
    description: "Which chart. Defaults to the selected one.",
  },
} as const;

const NO_ARGS = { type: "object", properties: {}, additionalProperties: false };

// ── the command search's helpers ────────────────────────────────────────────

/**
 * Where Paste symbols… files: the list named, else the list on screen, else
 * nothing — and nothing means **ask** (owner): the palette offers the user's
 * lists, because guessing one would file a clipboard somewhere unseen.
 */
export function pasteTarget(
  lists: readonly TrackList[],
  onScreen: TrackList | undefined,
  listId?: string,
): { list: TrackList } | { ask: true } | { refused: string } {
  if (listId) {
    const list = lists.find((l) => l.id === listId);
    return list ? { list } : { refused: "that list is gone" };
  }
  if (onScreen) return { list: lists.find((l) => l.id === onScreen.id) ?? onScreen };
  if (lists.length === 0) return { refused: "you have no track lists yet — make one from the ◎ picker first" };
  return { ask: true };
}

const trackOffered = (ctx: ActionContext) => ctx.trackListsOffered?.() ?? false;

/** A compiled answer from `args`, or the reason it is not one. */
function describedOf(args: Record<string, unknown>): Described | string {
  const source = str(args, "source");
  const expression = args["expression"];
  if (!source || !expression || typeof expression !== "object") {
    return "describe it first — this takes the compiled answer, not English";
  }
  const requires = Array.isArray(args["requires"])
    ? (args["requires"] as unknown[]).filter((r): r is string => typeof r === "string")
    : undefined;
  return { source, expression: expression as ScreenerExpr, ...(requires ? { requires } : {}) };
}

const askNo = (reason: string): AskOutcome => ({ ok: false, reason });

/** What the describer is told the screen has: its columns, and the user's own with their DSL. */
function describeColumnsOf(config: ScreenerConfig): DescribeColumn[] {
  return (config.columns ?? []).map((c) => ({
    field: c.field,
    ...(c.header ? { label: c.header } : {}),
    ...(c.computed ? { computed: c.computed.source ?? "" } : {}),
  }));
}

/**
 * Enter in a Describe box: one model call, its compiled DSL, and [Add] / [Edit].
 * Refused before the call where the answer could not be used anyway — no
 * screener, no plan — so a refusal never costs a call.
 */
async function askDescribe(ctx: ActionContext, text: string): Promise<AskOutcome> {
  const request = text.trim();
  if (!request) return askNo("type what it should find, in English");
  const describer = ctx.describer?.();
  if (!describer) return askNo("Describe is not included in your plan");
  const screener = ctx.screener?.();
  if (!screener) return askNo("there is no screener on this layout to add it to");
  const answer = await describer(request, describeColumnsOf(screener.config));
  if (!answer.ok) return askNo(answer.error.message);
  const args = {
    source: answer.source,
    expression: answer.expression,
    requires: answer.requires,
    request,
  };
  return {
    ok: true,
    preview: answer.source,
    offers: [
      { label: "Add", args },
      { label: "Edit", args: { ...args, edit: true } },
    ],
  };
}

const DESCRIBED_ARGS = {
  type: "object",
  properties: {
    source: { type: "string", description: "The compiled ScanScript, as the describer returned it." },
    expression: { type: "object", description: "The compiled expression tree." },
    requires: { type: "array", items: { type: "string" } },
    request: { type: "string", description: "The English it was described from — the name it is given." },
    edit: { type: "boolean", description: "Open the editor on it instead of adding it." },
  },
  required: ["source", "expression"],
  additionalProperties: false,
};

const DESCRIBE_GESTURE =
  "a description is turned into an expression from the command search, at the user's request, so ask the user to press Ctrl+K and pick Describe";

/** One indicator in `addIndicator`'s args — the top level, and each of `also`. */
const INDICATOR_ARGS = {
  indicator: { type: "string", description: "The indicator's catalog name or label, e.g. `macd`, `RSI`." },
  // Only the leading numeric params: a choice or symbol param ends the run and
  // keeps its default, as the palette's typed grammar does.
  params: {
    type: "array",
    items: { type: "number" },
    description: "Its leading numeric settings in order, e.g. [5, 35, 5] for MACD. Omitted ones keep their defaults.",
  },
  smooth: {
    type: "object",
    description:
      "A moving average of the indicator's first line (MACD's MACD line), drawn in its pane — one level, never of another average.",
    properties: {
      kind: { type: "string", enum: ["sma", "ema", "wma", "hma"] },
      length: { type: "integer", minimum: 2, maximum: 500 },
    },
    required: ["kind", "length"],
    additionalProperties: false,
  },
} as const;

const INDICATOR_ITEM = {
  type: "object",
  properties: INDICATOR_ARGS,
  required: ["indicator"],
  additionalProperties: false,
} as const;

/** One item of `addIndicator`'s args as a plan step, or the reason it is not one. */
function planItemOf(catalog: readonly IndicatorEntry[], raw: unknown): PlanItem | string {
  const args = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const wanted = str(args, "indicator")?.trim().toLowerCase();
  if (!wanted) return "name the indicator to add";
  // The name first, then the label: `macd` is unambiguous, and a label is
  // what a person types when they do not know the name.
  const entry = findEntry(catalog, wanted);
  if (!entry) return `there is no indicator called ${wanted}`;
  // The picker shows a locked family and refuses it; so does this. The
  // server would refuse the alias anyway — this says why, before it does.
  if (entry.locked) return `${entry.label} is not included in your plan`;
  const given = args["params"];
  if (given !== undefined && (!Array.isArray(given) || !given.every((n) => typeof n === "number" && Number.isFinite(n)))) {
    return "params is a list of numbers";
  }
  const params = (given as number[] | undefined) ?? [];
  const slots = numericSlots(entry);
  if (params.length > slots) {
    return slots === 0
      ? `${entry.label} takes no numeric settings`
      : `${entry.label} takes at most ${slots} number${slots === 1 ? "" : "s"}`;
  }
  const smooth = args["smooth"] as { kind?: unknown; length?: unknown } | undefined;
  if (smooth === undefined) return { entry, params };
  const { kind, length } = smooth ?? {};
  if (typeof kind !== "string" || typeof length !== "number" || !validSmooth(kind, length)) {
    return "smooth is an sma or ema with a whole length from 2 to 500";
  }
  return { entry, params, smooth: { kind, length } };
}

// ── the list ────────────────────────────────────────────────────────────────

export const ACTIONS: readonly Action[] = [
  // **The command search's rows lead the list** (owner's mock, 2026-10-18):
  // an empty box shows these five first, in this order, then the PIP and data
  // panel rows. The palette keeps the registry's order, so this order is it.
  {
    id: "addIndicator",
    title: "Add indicator",
    description:
      // Kept to plain words: the sub-search matches its rows against this too,
      // so an argument name here (`smooth`) made `sm` find every indicator.
      // The arguments are described in the schema, where the agent reads them.
      "Add an indicator to the chart — an overlay on the price, or a pane of its own, as the indicator picker would. Name it by the catalog's name or label, such as macd or rsi; its numeric settings, an average of its first line, and further indicators to add in the same step are optional.",
    keywords: ["indicator", "study", "add", "overlay", "oscillator"],
    // The discoverable way in. Typing `rsi` at the top still finds the row
    // directly; an empty box listed no indicator at all, which hid the whole
    // catalog from anybody who did not already know a name to type.
    palette: { mode: "browse", title: "Add indicator…", prompt: "Add indicator:", placeholder: "Search indicators" },
    hint: () => "pick from the indicator list",
    inputSchema: {
      type: "object",
      properties: {
        ...INDICATOR_ARGS,
        also: {
          type: "array",
          items: INDICATOR_ITEM,
          description: "More indicators added in the same step — one change, one undo for all of them.",
        },
        ...CHART_ARG,
      },
      required: ["indicator"],
      additionalProperties: false,
    },
    choices: (ctx) =>
      ctx.indicators().map((entry) => ({
        value: entry.name,
        label: entry.locked ? `${entry.label} (locked)` : entry.label,
        keywords: [entry.name, entry.category],
        detail: entry.category,
      })),
    run: (ctx, args) =>
      withChart(ctx, args, (group) => {
        if (!str(args, "indicator")?.trim()) return no("name the indicator to add");
        const catalog = ctx.indicators();
        if (catalog.length === 0) return no("the indicator list has not loaded yet — try again in a moment");
        // Every item checked before anything is added, so a pick is all or
        // nothing — and one `setPanes`, so one undo takes it all back.
        const also = Array.isArray(args["also"]) ? (args["also"] as unknown[]) : [];
        const items: PlanItem[] = [];
        for (const raw of [args, ...also]) {
          const item = planItemOf(catalog, raw);
          if (typeof item === "string") return no(item);
          items.push(item);
        }
        const planned = addIndicatorPlan(group, items, ctx.mintId);
        if ("reason" in planned) return no(planned.reason);
        const next = planned.group;
        ctx.dispatch({ type: "setPanes", chartGroupId: group.id, panes: next.panes });
        const before = new Set(group.panes.flatMap((p) => (p.plots ?? []).map((plot) => plot.id)));
        const aliases = next.panes
          .flatMap((p) => p.plots ?? [])
          .filter((plot) => !before.has(plot.id) && plot.outputAlias)
          .map((plot) => plot.outputAlias!);
        return ok({ chartGroupId: group.id, indicator: items[0]!.entry.name, aliases });
      }),
  },
  {
    id: "exportChartData",
    title: "Export chart data",
    description:
      "Download the selected chart's data as a CSV: every loaded bar (not only the ones on screen) with date, open, high, low, close and volume, then each plotted indicator under the name its legend shows.",
    keywords: ["export", "download", "csv", "data", "bars", "spreadsheet", "excel"],
    inputSchema: { type: "object", properties: { ...CHART_ARG }, additionalProperties: false },
    label: () => "Export chart data…",
    hint: () => "bars + indicators → CSV",
    // A download is started by a script without complaint in every browser that
    // matters, so this one *is* a tool call — the agent can export a chart.
    run: (ctx, args) =>
      withChart(ctx, args, (group) => {
        if (!ctx.chartTable || !ctx.download) return no("exporting is not available here");
        const table = ctx.chartTable(group.id);
        if (!table || barRows(table).length === 0) {
          return no("that chart has not loaded any bars yet — try again once it has drawn");
        }
        const fileName = chartExportFileName(table.symbol || group.symbol, table.frequency || group.frequency);
        ctx.download(chartExportCsv(table), fileName);
        return ok({ chartGroupId: group.id, fileName, bars: barRows(table).length });
      }),
  },
  {
    id: "exportSymbols",
    title: "Export symbols",
    description:
      "Open the export dialog for the screen on display — its symbols to the clipboard or a CSV, alone, with a price, or with every column showing.",
    keywords: ["export", "symbols", "screen", "list", "clipboard", "csv", "tickers"],
    inputSchema: NO_ARGS,
    label: () => "Export symbols…",
    hint: () => "this screen → clipboard/CSV",
    run: (ctx) => {
      const commands = ctx.screenerCommands?.();
      if (!commands) return no("there is no screener showing — show it first");
      if (!commands.exportSymbols) return no("the screen has no rows to export yet");
      commands.exportSymbols();
      return ok();
    },
  },
  {
    id: "copySymbol",
    title: "Copy symbol",
    description:
      "Copy the selected chart's symbol to the clipboard. A ratio copies as its canonical composite, such as AAPL/SPY, which pastes back into a track list as the same row.",
    keywords: ["copy", "symbol", "ticker", "clipboard"],
    inputSchema: { type: "object", properties: { ...CHART_ARG }, additionalProperties: false },
    // The active chart, named in the row, so the person sees what goes on the
    // clipboard before it does.
    label: (ctx) => {
      const group = ctx.activeChartGroupId ? ctx.chartGroup(ctx.activeChartGroupId) : undefined;
      return group?.symbol ? `Copy symbol — ${canonicalComposite(group.symbol)}` : "Copy symbol";
    },
    hint: (ctx) => {
      const group = ctx.activeChartGroupId ? ctx.chartGroup(ctx.activeChartGroupId) : undefined;
      return group?.symbol ? `${canonicalComposite(group.symbol)} → clipboard` : "the chart's symbol → clipboard";
    },
    // A clipboard write needs a real click. A tool call would report a copy the
    // browser refused, and the agent would tell the user it worked.
    gesture: "the clipboard can only be written from a real click, so ask the user to press Ctrl+K and pick Copy symbol",
    run: (ctx, args) =>
      withChart(ctx, args, (group) => {
        if (!ctx.copyText) return no("the clipboard is not available here");
        if (!group.symbol) return no("that chart has no symbol to copy");
        const symbol = canonicalComposite(group.symbol);
        ctx.copyText(symbol);
        return ok({ chartGroupId: group.id, symbol });
      }),
  },
  {
    id: "pasteSymbols",
    title: "Paste symbols",
    description:
      "Read symbols from the clipboard into one of your track lists — the list on screen, or one you pick — showing what will be added, already in and refused before anything is filed.",
    keywords: ["paste", "clipboard", "import", "track list", "watchlist", "symbols"],
    inputSchema: {
      type: "object",
      properties: { listId: { type: "string", description: "Which list. Defaults to the list on screen." } },
      additionalProperties: false,
    },
    available: trackOffered,
    hint: (ctx) => {
      const onScreen = ctx.screenerCommands?.()?.listOnScreen;
      return onScreen ? `clipboard → ${onScreen.name}` : "clipboard → a track list";
    },
    palette: {
      mode: "browse",
      title: "Paste symbols…",
      prompt: "Paste into:",
      placeholder: "Pick a list",
      // Asked only when no list is on screen (owner): with one showing, that is
      // the list, and a question would be one click too many.
      browseWhen: (ctx) => !ctx.screenerCommands?.()?.listOnScreen,
      nested: true,
    },
    choices: (ctx) => (ctx.trackLists?.() ?? []).map((list) => ({ value: list.id, label: list.name })),
    gesture: "the clipboard can only be read from a real click, so ask the user to press Ctrl+K and pick Paste symbols",
    run: (ctx, args) => {
      if (!trackOffered(ctx)) return no("track lists are not included in your plan");
      const commands = ctx.screenerCommands?.();
      if (!commands?.pasteInto) return no("there is no screener showing — show it to paste into a list");
      const target = pasteTarget(ctx.trackLists?.() ?? [], commands.listOnScreen, str(args, "listId"));
      if ("refused" in target) return no(target.refused);
      if ("ask" in target) return no("pick the list to paste into");
      commands.pasteInto(target.list);
      return ok({ listId: target.list.id });
    },
  },
  {
    id: "describeColumn",
    title: "Describe a column",
    description:
      "Describe a screener column in English and add it as a computed column — the column editor's Describe, from the command search. One AI call per description.",
    keywords: ["describe", "column", "computed", "english", "ai", "formula"],
    inputSchema: DESCRIBED_ARGS,
    available: (ctx) => ctx.describer?.() !== undefined,
    hint: () => "English → a computed column",
    palette: {
      mode: "text",
      title: "Describe a column…",
      prompt: "Describe a column:",
      placeholder: "e.g. distance from the 50-day average in percent — Enter to describe",
    },
    ask: askDescribe,
    gesture: DESCRIBE_GESTURE,
    run: (ctx, args) => {
      if (!ctx.describer?.()) return no("Describe is not included in your plan");
      const described = describedOf(args);
      if (typeof described === "string") return no(described);
      const screener = ctx.screener?.();
      if (!screener) return no("there is no screener on this layout to add it to");
      const { patch, field } = describedColumnPatch(
        screener.config,
        described,
        str(args, "request") ?? described.source,
      );
      // [Edit] adds it too: the column editor edits a column, so there is no
      // unsaved draft to hand it — the column is added and the editor opened on
      // it, and undo takes it away again in one step.
      ctx.dispatch({ type: "updateScreener", sectionId: screener.sectionId, patch });
      if (args["edit"] === true) ctx.openScreenerEditor?.("columns");
      return ok({ field });
    },
  },
  {
    id: "describeCondition",
    title: "Describe a condition",
    description:
      "Describe a screener condition in English, add it to the screen's conditions and turn it on. One AI call per description.",
    keywords: ["describe", "condition", "filter", "english", "ai", "rule"],
    inputSchema: DESCRIBED_ARGS,
    available: (ctx) => ctx.describer?.() !== undefined,
    hint: () => "English → a condition, turned on",
    palette: {
      mode: "text",
      title: "Describe a condition…",
      prompt: "Describe a condition:",
      placeholder: "e.g. closed above the 200-day average — Enter to describe",
    },
    ask: askDescribe,
    gesture: DESCRIBE_GESTURE,
    run: (ctx, args) => {
      if (!ctx.describer?.()) return no("Describe is not included in your plan");
      const described = describedOf(args);
      if (typeof described === "string") return no(described);
      const screener = ctx.screener?.();
      if (!screener) return no("there is no screener on this layout to add it to");
      if (args["edit"] === true) {
        // Loaded into the filter editor unsaved, which is what [Edit] means: the
        // person changes it, then saves it under a name of their own.
        ctx.dispatch({
          type: "updateScreener",
          sectionId: screener.sectionId,
          patch: describedConditionEditPatch(described),
        });
        ctx.openScreenerEditor?.("filters");
        return ok({ edit: true });
      }
      const { patch, id } = describedConditionPatch(
        screener.config,
        described,
        str(args, "request") ?? described.source,
        ctx.mintId,
      );
      ctx.dispatch({ type: "updateScreener", sectionId: screener.sectionId, patch });
      return ok({ id });
    },
  },
  {
    id: "goToSymbol",
    title: "Go to",
    description:
      "Chart a symbol on the selected chart — a ticker such as AVGO, or a ratio such as AVGO/SPY. If that chart's symbol is locked it goes to the next unlocked chart instead; only a symbol in the user's plan can be charted.",
    keywords: ["symbol", "ticker", "chart", "go to", "open", "ratio", "compare"],
    inputSchema: one(
      "symbol",
      { type: "string", description: "A ticker (`AVGO`) or a ratio of tickers (`AVGO/SPY`)." },
      true,
    ),
    // Rows from the symbol index as the person types (`typedCommands`), ranked
    // as `SymbolDialog` ranks them.
    typed: true,
    available: (ctx) => ctx.chartSymbol !== undefined,
    run: (ctx, args) => {
      let symbol = committedSymbol(str(args, "symbol") ?? "");
      if (!symbol) return no("name the symbol to chart");
      if (!ctx.chartSymbol) return no("charting a symbol is not available here");
      const chartGroupId = str(args, "chartGroupId") ?? ctx.activeChartGroupId;
      if (!chartGroupId || !ctx.chartGroup(chartGroupId)) return no("there is no chart to act on");
      // **Only a symbol this account may chart** — `SymbolDialog`'s check, and
      // its sentence. Without a symbol service there is no plan to keep anybody
      // inside, so what was asked is charted, as the dialog does.
      if (ctx.symbols) {
        const rows = ctx.symbols();
        if (!rows) return no("symbols are still loading — try again in a moment");
        const leg = firstOutside(symbol, symbolResolver(rows)!);
        if (leg !== null) return no(refusalMessage(symbol, leg, undefined));
        // A plain ticker takes the index's own spelling, as a picked row would:
        // the agent's `avgo` charts `AVGO`, not a lower-case twin of it.
        const upper = symbol.toUpperCase();
        symbol = rows.find((r) => r.symbol.toUpperCase() === upper)?.symbol ?? symbol;
      }
      const route = ctx.chartSymbol(chartGroupId, symbol);
      if (route.kind === "locked") {
        return no(`every chart on this layout has its symbol locked — unlock one to chart ${symbol}`);
      }
      return ok({ chartGroupId: route.chartGroupId, symbol });
    },
  },
  {
    id: "setInterval",
    title: "Interval",
    description:
      "Change the selected chart's interval — a count and a unit: 15mi is fifteen minutes, 4h, 2d, 1w, and 3m is three months (minute is `mi`, month is `m`). Any count works, not only 1: an \"8D\" or \"8-day\" period is `8d` (eight-day bars), the same as the toolbar's count box set to 8 beside D. Charts whose interval is linked follow it.",
    keywords: ["interval", "timeframe", "frequency", "bar size"],
    inputSchema: one(
      "interval",
      { type: "string", description: "`15mi`, `1h`, `1d`, `2d`, `1w`, `1m` (a month) — any count on those units." },
      true,
    ),
    // Rows from the typed text through the toolbar's parser, and the toolbar
    // menu's presets on `interval` (`typedCommands`).
    typed: true,
    run: (ctx, args) =>
      withChart(ctx, args, (group) => {
        const raw = str(args, "interval");
        // `canonicalInterval`, the one input rule the toolbar's recents use:
        // any casing of the stored vocabulary and nothing else.
        const frequency = canonicalInterval(raw?.replace(/\s+/g, ""));
        if (!frequency) {
          return no(
            `${raw ? `“${raw}”` : "that"} is not an interval — a count and a unit: 15mi (minutes), 4h, 2d, 1w, 3m (months)`,
          );
        }
        // The toolbar's own dispatch, so linked intervals (`sync.frequency`)
        // fan out in the reducer exactly as they do there.
        ctx.dispatch({ type: "setFrequency", chartGroupId: group.id, frequency });
        return ok({ chartGroupId: group.id, frequency });
      }),
  },
  {
    id: "openPip",
    title: "Show picture-in-picture",
    description:
      "Show a small panel inside the chart. By default it draws the next timeframe up — the weekly on a daily chart — but it can show the symbol's profile, peers, fundamentals, a summary or news instead.",
    keywords: ["pip", "picture in picture", "mini chart", "higher timeframe", "weekly", "inset"],
    inputSchema: {
      type: "object",
      properties: {
        view: { type: "string", enum: [...PIP_VIEWS], description: "What the panel shows." },
        frequency: {
          type: "string",
          description: "Interval for the chart view. Must be coarser than the chart's own.",
        },
        ...CHART_ARG,
      },
      additionalProperties: false,
    },
    run: (ctx, args) =>
      withPipChart(ctx, args, (group) => {
        const view = (str(args, "view") ?? "chart") as PipView;
        if (!PIP_VIEWS.includes(view)) return no(`${view} is not a view this panel can show`);
        const frequency = str(args, "frequency") as Frequency | undefined;
        if (view === "chart") {
          const resolved = pipFrequencyFor(group.frequency, frequency ?? group.pipFrequency);
          if (!resolved) {
            return no(
              `there is no timeframe above ${group.frequency}, so a chart panel would draw the same bars twice`,
            );
          }
          ctx.dispatch({ type: "setPipFrequency", chartGroupId: group.id, frequency: resolved });
        }
        ctx.dispatch({ type: "setPipView", chartGroupId: group.id, view });
        return ok({ chartGroupId: group.id, view });
      }),
  },
  {
    id: "closePip",
    title: "Hide picture-in-picture",
    description: "Hide the small panel inside the chart. Its corner and size are remembered.",
    keywords: ["pip", "hide", "close", "picture in picture"],
    inputSchema: { type: "object", properties: { ...CHART_ARG }, additionalProperties: false },
    run: (ctx, args) =>
      withChart(ctx, args, (group) => {
        if (!group.pipView) return no("that chart has no picture-in-picture panel open");
        ctx.dispatch({ type: "setPipView", chartGroupId: group.id, view: null });
        return ok({ chartGroupId: group.id });
      }),
  },
  {
    id: "setPipView",
    title: "Change what the picture-in-picture shows",
    description:
      "Switch the small panel between the higher-timeframe chart, the profile, peers, fundamentals, a summary and news.",
    keywords: ["pip", "view", "profile", "peers", "fundamentals", "summary", "news", "sentiment"],
    inputSchema: one("view", { type: "string", enum: [...PIP_VIEWS] }, true),
    run: (ctx, args) =>
      withPipChart(ctx, args, (group) => {
        const view = str(args, "view") as PipView | undefined;
        if (!view || !PIP_VIEWS.includes(view)) return no("name one of the panel's views");
        ctx.dispatch({ type: "setPipView", chartGroupId: group.id, view });
        return ok({ chartGroupId: group.id, view });
      }),
  },
  {
    id: "setPipFrequency",
    title: "Change the picture-in-picture timeframe",
    description:
      "Set the interval the small chart panel draws. It must be coarser than the chart's own, because the panel exists to show the timeframe above.",
    keywords: ["pip", "timeframe", "interval", "weekly", "monthly"],
    inputSchema: one("frequency", { type: "string" }, true),
    run: (ctx, args) =>
      withPipChart(ctx, args, (group) => {
        const frequency = str(args, "frequency") as Frequency | undefined;
        if (!frequency) return no("name an interval");
        const resolved = pipFrequencyFor(group.frequency, frequency);
        if (resolved !== frequency) {
          return no(
            `${frequency} is not coarser than the chart's ${group.frequency} — the panel shows the timeframe above, so it would draw the same bars twice`,
          );
        }
        ctx.dispatch({ type: "setPipFrequency", chartGroupId: group.id, frequency });
        return ok({ chartGroupId: group.id, frequency });
      }),
  },
  {
    id: "setPipCorner",
    title: "Move the picture-in-picture",
    description:
      "Put the small panel in a named corner, or `auto` to let it choose the emptier of the two left-hand corners by counting the bars in each.",
    keywords: ["pip", "corner", "position", "move", "top left", "bottom left"],
    inputSchema: one("corner", { type: "string", enum: [...PIP_CORNERS] }, true),
    run: (ctx, args) =>
      withPipChart(ctx, args, (group) => {
        const corner = str(args, "corner") as PipCorner | undefined;
        if (!corner || !PIP_CORNERS.includes(corner)) return no("name a corner, or `auto`");
        ctx.dispatch({ type: "setPipCorner", chartGroupId: group.id, corner });
        return ok({ chartGroupId: group.id, corner });
      }),
  },
  {
    id: "setPipSize",
    title: "Resize the picture-in-picture",
    description: "Make the small panel small, medium or large.",
    keywords: ["pip", "size", "bigger", "smaller", "resize"],
    inputSchema: one("size", { type: "string", enum: [...PIP_SIZES] }, true),
    run: (ctx, args) =>
      withPipChart(ctx, args, (group) => {
        const size = str(args, "size") as PipSize | undefined;
        if (!size || !PIP_SIZES.includes(size)) return no("name a size: s, m or l");
        ctx.dispatch({ type: "setPipSize", chartGroupId: group.id, size });
        return ok({ chartGroupId: group.id, size });
      }),
  },
  {
    id: "swapPipTimeframe",
    title: "Promote the picture-in-picture timeframe",
    description:
      "Make the small panel's interval the chart's own, and move the panel up to the next one above it. Not an exchange: putting the chart's finer interval in the panel is the one thing the panel must never show.",
    keywords: ["pip", "swap", "promote", "zoom out", "step up"],
    inputSchema: { type: "object", properties: { ...CHART_ARG }, additionalProperties: false },
    run: (ctx, args) =>
      withPipChart(ctx, args, (group) => {
        if (group.pipView !== "chart") return no("the panel is not showing a chart");
        const shown = pipFrequencyFor(group.frequency, group.pipFrequency);
        const swapped = shown ? swapWithChart(group.frequency, shown) : null;
        if (!shown || !swapped) {
          return no(`there is no timeframe above ${shown ?? group.frequency} to move the panel to`);
        }
        ctx.dispatch({ type: "setFrequency", chartGroupId: group.id, frequency: swapped.chart });
        ctx.dispatch({ type: "setPipFrequency", chartGroupId: group.id, frequency: swapped.pip });
        return ok({ chartGroupId: group.id, ...swapped });
      }),
  },
  {
    id: "openDataPanel",
    title: "Open the data panel",
    description:
      "Open the panel of everything known about the charted symbol — profile, peers, fundamentals, a summary, news and sentiment — optionally on a named tab.",
    keywords: ["data", "info", "details", "about", "company", "profile", "panel"],
    inputSchema: {
      type: "object",
      properties: {
        tab: { type: "string", enum: [...PIP_VIEWS].filter((v) => v !== "chart") },
        ...CHART_ARG,
      },
      additionalProperties: false,
    },
    run: (ctx, args) =>
      withChart(ctx, args, (group) => {
        const tab = str(args, "tab") as PipView | undefined;
        if (tab !== undefined && (!PIP_VIEWS.includes(tab) || !isDataTab(tab))) {
          return no(`${tab} is not a tab the data panel has`);
        }
        ctx.dataPanel.openOn(group.id, tab);
        return ok({ chartGroupId: group.id, ...(tab ? { tab } : {}) });
      }),
  },
  {
    id: "setDataPanelTab",
    title: "Switch the data panel tab",
    description: "Show a different tab in the data panel, without moving it or closing it.",
    keywords: ["tab", "data", "profile", "peers", "fundamentals", "summary", "news", "sentiment"],
    inputSchema: {
      type: "object",
      properties: { tab: { type: "string", enum: [...PIP_VIEWS].filter((v) => v !== "chart") } },
      required: ["tab"],
      additionalProperties: false,
    },
    run: (ctx, args) => {
      const tab = str(args, "tab") as PipView | undefined;
      if (!tab || !PIP_VIEWS.includes(tab) || !isDataTab(tab)) {
        return no("name one of the data panel's tabs");
      }
      ctx.dataPanel.setTab(tab);
      return ok({ tab });
    },
  },
  {
    id: "closeDataPanel",
    title: "Close the data panel",
    description: "Close the data panel, wherever it is — over the chart or in its own window.",
    keywords: ["close", "dismiss", "data", "panel"],
    inputSchema: NO_ARGS,
    run: (ctx) => {
      ctx.dataPanel.close();
      return ok();
    },
  },
  {
    id: "dockDataPanel",
    title: "Bring the data panel back",
    description:
      "Bring a detached data panel back into the main window. Closing its window does the same thing.",
    keywords: ["dock", "return", "bring back", "re-attach", "window"],
    inputSchema: NO_ARGS,
    run: (ctx) => {
      ctx.dataPanel.dock();
      return ok();
    },
  },
  {
    id: "detachDataPanel",
    title: "Open the data panel in its own window",
    description:
      "Move the data panel into a separate window that can be dragged to another monitor.",
    keywords: ["detach", "window", "second monitor", "pop out", "separate"],
    inputSchema: NO_ARGS,
    // **The one action with no tool form.** A browser opens a window only from a
    // real click, so a tool call would report success against a window that never
    // appeared — the worst of the three possible outcomes, because the agent then
    // tells the user it worked. It stays in the registry so the UI and the search
    // both still have it.
    gesture:
      "a separate window can only be opened by a real click, so ask the user to press the ⧉ button on the data panel",
    run: (ctx) => {
      ctx.dataPanel.detach();
      return ok();
    },
  },
  {
    id: "selectGroups",
    title: "Select groups",
    description:
      "Choose which groups the screener screens — its ◎ universe — by name or code, such as S&P 500 (SP500), Russell 2000 (RU2000), Nasdaq 100 (NQ100) or Nifty 50 (IN_NIFTY_50). combine \"or\" screens a symbol in any of them (union), \"and\" only one in all of them (intersection). The groups replace the ones picked now unless add is true. All the groups must be in one market.",
    keywords: ["group", "groups", "universe", "index", "sp500", "russell", "nasdaq", "screen", "union", "intersect"],
    inputSchema: {
      type: "object",
      properties: {
        groups: {
          type: "array",
          items: { type: "string" },
          minItems: 1,
          description: "Group names or codes — `S&P 500`, `sp500`, `RU2000`.",
        },
        combine: {
          type: "string",
          enum: ["or", "and"],
          description: "`or` (default) — in any group; `and` — in every group.",
        },
        add: { type: "boolean", description: "Keep the groups picked now and add these. Default false." },
      },
      required: ["groups"],
      additionalProperties: false,
    },
    // Rows from the typed phrase (`groupCommands`): "select group sp500 and russell 2000".
    typed: true,
    available: (ctx) => ctx.screenerCommands?.()?.selectGroups !== undefined,
    run: (ctx, args) => {
      const commands = ctx.screenerCommands?.();
      if (!commands?.selectGroups || !commands.groups) return no("there is no screener on this layout");
      const names = Array.isArray(args["groups"]) ? args["groups"].filter((g): g is string => typeof g === "string") : [];
      if (names.length === 0) return no("name the groups to select");
      const all = commands.groups();
      const groups: typeof all[number][] = [];
      for (const name of names) {
        const group = resolveGroup(all, name);
        if (!group) return no(`there is no group called “${name}”`);
        if (!groups.includes(group)) groups.push(group);
      }
      const locked = groups.find((g) => g.locked);
      if (locked) return no(`${locked.name} is not included in your plan`);
      const markets = new Set(groups.map((g) => g.market ?? "US"));
      if (markets.size > 1) {
        return no(`${groups.map((g) => g.name).join(" and ")} are in different markets — a screen covers one market`);
      }
      const combine = str(args, "combine") === "and" ? "and" : "or";
      commands.selectGroups(groups, combine, args["add"] === true);
      return ok({ groups: groups.map((g) => g.code), combine });
    },
  },
  {
    id: "openGroupPicker",
    title: "Open the group list",
    description: "Open the screener's ◎ universe picker, to tick groups by hand.",
    keywords: ["group", "groups", "universe", "list", "pick", "◎"],
    inputSchema: NO_ARGS,
    available: (ctx) => ctx.screenerCommands?.()?.openGroupPicker !== undefined,
    run: (ctx) => {
      const open = ctx.screenerCommands?.()?.openGroupPicker;
      if (!open) return no("there is no screener group bar on screen");
      open();
      return ok();
    },
  },
  {
    id: "openGuide",
    title: "Guide",
    description: "Open the user guide in a new tab at one section.",
    keywords: ["guide", "help", "how", "where"],
    inputSchema: one("anchor", { type: "string", description: "The section's anchor, from findInGuide." }, true),
    // Rows from the guide's search (`guideCommands`), never listed up front.
    typed: true,
    available: (ctx) => ctx.openGuideAt !== undefined,
    gesture: "a new tab can only be opened by a real click, so tell the user the section's name and path instead",
    run: (ctx, args) => {
      const anchor = str(args, "anchor");
      if (!anchor || !ctx.openGuideAt) return no("the user guide is not available here");
      ctx.openGuideAt(anchor);
      return ok();
    },
  },
  {
    id: "stylePlot",
    title: "Style a plot",
    description:
      "Change how lines or markers already on the chart are drawn: marker size, line width, dash, marker shape or colour. Name the plots by the ids in the chart context; the context's style shows each one's current values, and an absent markerSize is the default of 7 pixels. Give only the fields to change. Several plots take the same change in one step — one undo for all of them.",
    keywords: ["style", "size", "width", "thickness", "marker", "colour", "color", "dash"],
    inputSchema: {
      type: "object",
      properties: {
        plotIds: {
          type: "array",
          items: { type: "string" },
          minItems: 1,
          description: "The plots to restyle, by id from the chart context.",
        },
        markerSize: {
          type: "number",
          minimum: MARKER_SIZE.min,
          maximum: MARKER_SIZE.max,
          description: "Marker size in pixels for a scatter or signal plot.",
        },
        thickness: { type: "number", minimum: 0, maximum: 10, description: "Line width in pixels." },
        dashStyle: { type: "string", enum: ["solid", "dashed", "dotted"] },
        markerShape: { type: "string", enum: [...MARKER_SHAPES] },
        stroke: {
          type: "string",
          description: "Colour: a theme token such as accent.blue, positive or negative (follows a theme switch), or a hex such as #FF8800 (does not).",
        },
        ...CHART_ARG,
      },
      required: ["plotIds"],
      additionalProperties: false,
    },
    // The chat's: the picker's row editor is this action's UI.
    typed: true,
    run: (ctx, args) =>
      withChart(ctx, args, (group) => {
        const planned = stylePlotsPlan(group, args);
        if (typeof planned === "string") return no(planned);
        ctx.dispatch({ type: "setPanes", chartGroupId: group.id, panes: planned.panes });
        return ok({ chartGroupId: group.id, plotIds: planned.plotIds, style: planned.style });
      }),
  },
  {
    id: "linkConditions",
    title: "Link conditions from a screen",
    description:
      "Link conditions from one of your groups' screens (such as Persons) into the screen on the screener, as Conditions › Link from screen… does: each stays the group's rule, evaluated on the server and kept in step when the group changes it. They arrive unticked. Name the source screen; name conditions to link only some, or none for all of them. Not offered while authoring a group's own screen.",
    keywords: ["link", "conditions", "screen", "filters"],
    inputSchema: {
      type: "object",
      properties: {
        fromScreen: { type: "string", description: "The source screen's name, such as Persons, or Group · Name." },
        conditions: {
          type: "array",
          items: { type: "string" },
          description: "Condition names to link. Omit for every condition on that screen.",
        },
      },
      required: ["fromScreen"],
      additionalProperties: false,
    },
    // The chat's: the Conditions tab's picker is this action's UI, and a load
    // stands between the ask and the change.
    typed: true,
    available: (ctx) => ctx.screenerLinks !== undefined,
    lookup: async (ctx, args) => {
      const asked = str(args, "fromScreen")?.trim();
      if (!asked) return no("name the screen to link from");
      const panel = ctx.screenerLinks?.();
      if (!panel) return no("there is no screener in this layout");
      if (!panel.links) {
        return no(
          "this is a group's screen you are authoring — links can't be published there; use Copy from screen… in the Conditions tab instead",
        );
      }
      const found = matchScreen(panel.links.screens, asked, panel.screenRef);
      if (typeof found === "string") return no(found);
      const definition = await panel.links.load(found.key);
      if (!definition) return no(`${found.name} can't be read from this account`);
      const library = definition.savedFilters ?? [];
      if (library.length === 0) return no(`${found.name} has no conditions to link`);
      const wanted = Array.isArray(args["conditions"])
        ? (args["conditions"] as unknown[]).filter((c): c is string => typeof c === "string")
        : [];
      const byName = (name: string) =>
        library.find((f) => f.name.trim().toLowerCase() === name.trim().toLowerCase());
      const unknown = wanted.filter((name) => !byName(name));
      if (unknown.length > 0) {
        return no(
          `${found.name} has no condition named ${unknown.join(", ")} — it has ${library.map((f) => f.name).join(", ")}`,
        );
      }
      const picked = wanted.length > 0 ? wanted.map((name) => byName(name)!) : [...library];
      // Re-read after the round trip: the panel may have moved to another screen.
      const now = ctx.screenerLinks?.();
      if (!now || now.sectionId !== panel.sectionId || now.screenRef !== panel.screenRef) {
        return no("the screen on the screener changed while the conditions were loading — ask again");
      }
      const next = linkConditions(now.saved, picked, found.key);
      const skipped = new Set(next.skipped);
      const linked = picked.map((f) => f.name).filter((name) => !skipped.has(name));
      if (linked.length > 0) {
        ctx.dispatch({ type: "updateScreener", sectionId: now.sectionId, patch: { savedFilters: next.savedFilters } });
      }
      return ok({
        from: `${found.ownerName} · ${found.name}`,
        into: now.screenName ?? now.screenRef,
        linked,
        alreadyOnScreen: next.skipped,
      });
    },
    run: () => no("this answers the AI chat only — use Conditions › Link from screen…"),
  },
  {
    id: "addCondition",
    title: "Add a condition to the screen",
    description:
      "Add a new condition to the screen on the screener, as the Conditions tab's Save as new does: a name and a ScanScript expression that is true or false per row, such as c > c.sma50 and rsi(c, 14) < 30. Compiled first; one that doesn't compile changes nothing and the error comes back. It arrives unticked. While authoring a group's screen it goes into the draft. " +
      // The owner's report, 2026-11-02: asked for "weekly rsi", the chat wrote
      // rsi(weekly, 14), which compiles — `weekly` reads as a column name — and
      // means nothing. The rules the Describe prompt carries, in brief.
      "ScanScript: lowercase; o h l c v; postfix is preferred — c.sma20 is sma(c, 20); always write the input (c.rsi14, not rsi14, which names a column). " +
      "A frequency goes on the input, never as an argument: c.w.rsi14 or w.rsi(14) is weekly RSI(14), c.m.sma10 monthly; units d w m q y, mi is minute. Absent means daily. " +
      "[n] looks back n bars of that frequency: w.rsi(14)[1] is last week's weekly RSI, c[1] yesterday's close. " +
      "Example — weekly RSI crossing above 70: w.rsi(14)[1] < 70 and w.rsi(14) > 70. Operators + - * / %, == != > >= < <=, and/or, parentheses.",
    keywords: ["add", "new", "condition", "filter", "scanscript", "screen"],
    inputSchema: {
      type: "object",
      properties: {
        name: { type: "string", description: "The condition's name, as the picker lists it." },
        expression: { type: "string", description: "ScanScript, true or false per row." },
        category: { type: "string", description: "The picker category to file it under. Optional." },
      },
      required: ["name", "expression"],
      additionalProperties: false,
    },
    // The chat's: a compile stands between the ask and the change.
    typed: true,
    available: (ctx) => ctx.screenerLinks !== undefined && ctx.compileExpr !== undefined,
    lookup: async (ctx, args) => {
      const name = str(args, "name")?.trim();
      const source = str(args, "expression")?.trim();
      const category = str(args, "category")?.trim();
      if (!name) return no("name the condition");
      if (!source) return no("give the condition's ScanScript expression");
      const panel = ctx.screenerLinks?.();
      if (!panel) return no("there is no screener in this layout");
      if (!ctx.compileExpr) return no("the ScanScript compiler isn't available here");
      const taken = panel.saved.find((f) => f.name.trim().toLowerCase() === name.toLowerCase());
      if (taken) return no(`this screen already has a condition named ${taken.name} — pick another name`);
      const compiled = await ctx.compileExpr(source);
      if (!compiled.ok) return no(`${source} doesn't compile: ${compiled.error.message}`);
      // `rsi(weekly, 14)` compiles — with no field list the compiler takes
      // `weekly` for a column — and screens on nothing. No column is named for a
      // period, so one read as a field is the frequency in the wrong place.
      const period = compiled.requires.find((f) => FREQUENCY_WORDS.test(f));
      if (period) {
        return no(
          `${period} isn't a column — a frequency goes on the input: w.rsi(14) or c.w.rsi14 is weekly, [1] the bar before (w.rsi(14)[1] is last week's)`,
        );
      }
      // Re-read after the round trip: the panel may have moved to another screen.
      const now = ctx.screenerLinks?.();
      if (!now || now.sectionId !== panel.sectionId || now.screenRef !== panel.screenRef) {
        return no("the screen on the screener changed while the condition was compiling — ask again");
      }
      if (now.saved.some((f) => f.name.trim().toLowerCase() === name.toLowerCase())) {
        return no(`this screen already has a condition named ${name} — pick another name`);
      }
      const entry: SavedScreenerFilter = {
        id: ctx.mintId(),
        name,
        filter: compiled.expression,
        ...(category ? { category } : {}),
      };
      ctx.dispatch({
        type: "updateScreener",
        sectionId: now.sectionId,
        patch: { savedFilters: [...now.saved, entry] },
      });
      return ok({
        added: name,
        expression: compiled.source,
        into: now.screenName ?? now.screenRef,
        ...(category ? { category } : {}),
      });
    },
    run: () => no("this answers the AI chat only — use the Conditions tab's Save as new"),
  },
  {
    id: "findInGuide",
    title: "Find in the user guide",
    description:
      "Search the app's user guide. Call this whenever the user asks where something is in the app, what a screen or tab shows, or how to do something, and answer from what it returns — the guide's own names and paths (such as Data panel › Fin St). Pass the subject in a few words, such as \"fundamentals\" or \"export symbols\". Never describe a menu, button or tab the guide did not mention.",
    keywords: ["guide", "help", "where", "how", "find"],
    inputSchema: {
      type: "object",
      properties: { query: { type: "string", description: "What to look up, in a few words." } },
      required: ["query"],
      additionalProperties: false,
    },
    // An answer for the agent; the palette's Guide rows are `openGuide`.
    typed: true,
    available: (ctx) => ctx.loadGuide !== undefined,
    lookup: async (ctx, args) => {
      const query = str(args, "query")?.trim();
      if (!query) return no("say what to look up");
      if (!ctx.loadGuide) return no("the user guide is not available here");
      const found = searchGuide(await ctx.loadGuide(), query, 3);
      if (found.length === 0) return no(`nothing in the user guide matches “${query}” — try fewer or other words`);
      return ok({ results: found.map((entry) => entryForAgent(entry)) });
    },
    run: () => no("this answers the AI chat only"),
  },
];

/** One action by id. */
export function actionById(id: string): Action | undefined {
  return ACTIONS.find((a) => a.id === id);
}

/**
 * The actions a typed query matches, best first.
 *
 * Matches the **description** as well as the title, which is the rule the
 * indicator picker already earns its keep with — it is how "convergence" finds
 * MACD, and here it is how "what can this chart show me" finds the data panel.
 *
 * Ranked rather than filtered, because a title match is a different kind of hit
 * from a word buried in prose: someone typing `pip` means the panel, not every
 * action whose description mentions it.
 */
export interface Searchable {
  title: string;
  keywords: readonly string[];
  description: string;
}

/**
 * How well one entry answers a typed query, `0` for not at all.
 *
 * Shared by the action list and by the command palette derived from it, so the
 * two cannot rank the same words differently — which they would within a week of
 * being two copies.
 *
 * The tiers are the point. A title match is a different kind of hit from a word
 * buried in prose: someone typing `pip` means the panel, not every action whose
 * description happens to mention one.
 */
export function matchScore(query: string, item: Searchable): number {
  const q = query.trim().toLowerCase();
  if (!q) return 1;
  const title = item.title.toLowerCase();
  if (title.startsWith(q)) return 4;
  if (title.includes(q)) return 3;
  if (item.keywords.some((k) => k.toLowerCase().includes(q))) return 2;
  if (item.description.toLowerCase().includes(q)) return 1;
  return 0;
}

export function searchActions(query: string, actions: readonly Action[] = ACTIONS): Action[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...actions];
  const scored: { action: Action; score: number }[] = [];
  for (const action of actions) {
    const score = matchScore(q, action);
    if (score > 0) scored.push({ action, score });
  }
  // Stable within a score: the registry's own order is the order these were
  // designed in, and re-sorting equal hits alphabetically would scatter the PIP
  // actions through the list.
  return scored.sort((a, b) => b.score - a.score).map((s) => s.action);
}

/**
 * The registry as the tool manifest the bridge sends on connect.
 *
 * A refusal becomes a **thrown** error, because that is how `QueVeeClient` hands
 * a message back to the agent — "throwing is a normal outcome", per its own
 * contract — so the agent reads the reason and corrects itself rather than
 * seeing a success it cannot square with the screen.
 */
export function clientTools(ctx: ActionContext, actions: readonly Action[] = ACTIONS): ClientTool[] {
  return actions.map((action) => ({
    name: action.id,
    description: action.gesture
      ? `${action.description} NOTE: this cannot be done from a tool call — ${action.gesture}.`
      : action.description,
    inputSchema: action.inputSchema,
    handler: action.lookup
      ? async (args: Record<string, unknown>): Promise<ToolResultBody> => {
          const outcome = await action.lookup!(ctx, args ?? {});
          if (!outcome.ok) throw new Error(outcome.reason);
          return outcome.detail;
        }
      : (args: Record<string, unknown>): ToolResultBody => {
          if (action.gesture) throw new Error(action.gesture);
          const outcome = action.run(ctx, args ?? {});
          if (!outcome.ok) throw new Error(outcome.reason);
          return outcome.detail;
        },
  }));
}
